import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { addAppealSummon } from "services/prosecutorDecipilineService";
import { Appeal, Complaints, Summon } from "types";
import { LoadingIcon } from "utils/constant";
import "../../complaintRequest/styles/AddVictimModal.css";
import { useTranslation } from "contexts/i18nContext";
import { useAuth } from "contexts/AuthContext";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  appealData: Appeal;
  complaintData: Complaints;
  onAppealSummonRefresh: any;
}

export default function AddAppealSummonModal({
  isOpen,
  setIsOpen,
  appealData,
  complaintData,
  onAppealSummonRefresh,
}: ModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState<Summon>({
    hearing_date: "",
    region: "",
    zone: "",
    woreda: "",
    kebele: "",
    summary: "",
    files: [] as File[],
    investigation: null,
  });

  const [stampUrl, setStampUrl] = useState<string | null>(null);
  const [stampBlob, setStampBlob] = useState<Blob | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadStamp = async () => {
      try {
        // Construct the actual API URL – adjust to your real endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL_2}organization/service-stamp/read_stamp/?admin_unit_uuid=${user.admin_unit}&service_name=pdms`,
          {
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("authToken")}`,
            }, // if needed
          },
        );
        if (!response.ok) throw new Error("Failed to fetch stamp");
        const blob = await response.blob(); // ✅ direct binary, no string corruption
        setStampBlob(blob);
        objectUrl = URL.createObjectURL(blob);
        setStampUrl(objectUrl);
      } catch (error) {
        console.error("Failed to load stamp", error);
      }
    };

    if (isOpen && user) loadStamp();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [isOpen, user]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (!appealData?.id) {
      showToast(t("appealNotFound"), "error");
      return;
    }
    if (!stampBlob) {
      showToast("Stamp is required.", "error");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("hearing_date", formState?.hearing_date);
    data.append("region", formState?.region);
    data.append("zone", formState?.zone);
    data.append("woreda", formState?.woreda);
    data.append("kebele", formState?.kebele);
    data.append("summary", formState?.summary);
    data.append("summon_files", formState?.files[0] || "");
    data.append("signature1", stampBlob, "stamp.png");

    data.append("appeal_request", appealData?.id);
    data.append(
      "accused_prosecutor_name",
      `${complaintData?.accused_prosecutor_name || ""}`,
    );
    data.append(
      "user_id_accused_prosecutor_name",
      complaintData?.accused_prosecutor_admin_unit_id || "",
    );
    data.append(
      "assigned_prosecutor",
      complaintData?.assigned_prosecutor || "",
    );
    data.append(
      "user_id_assigned_prosecutor",
      complaintData?.user_id_assigned_prosecutor || "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");

    const isFormData = true;

    try {
      await addAppealSummon(data, isFormData);

      await Promise.resolve(onAppealSummonRefresh?.());
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");
      setFormState({
        hearing_date: "",
        region: "",
        zone: "",
        woreda: "",
        kebele: "",
        summary: "",
        files: [] as File[],
        investigation: null,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("createAppealSummon")}
    >
      <form onSubmit={handleSubmit} className="victim-form">
        <section className="victim-section">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("hearingDate")}
            </label>
            <input
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              type={"date"}
              name="hearing_date"
              placeholder={t("egJune122014")}
              value={formState.hearing_date}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("region")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="region"
                value={formState.region}
                onChange={handleChange}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("zone")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="zone"
                value={formState.zone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("woreda")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="woreda"
                value={formState.woreda}
                onChange={handleChange}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("kebele")}
              </label>
              <input
                required
                className="w-full border bg-gray-100 rounded-md p-2 text-sm"
                type="text"
                name="kebele"
                value={formState.kebele}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("summary")}
            </label>
            <textarea
              required
              rows={3}
              name="summary"
              value={formState.summary}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <section className="evidence-section">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("summonFile")}
          </label>
          <div className="file-upload-area" onClick={triggerFileInput}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              // multiple
              style={{ display: "none" }}
            />
            <p className="upload-text">{t("uploadFiles")}</p>
            {formState?.files && formState?.files?.length > 0 && (
              <div className="file-list">
                {formState?.files?.map((file, index) => (
                  <div key={index} className="file-item">
                    {file.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {stampUrl && (
          <div className="my-4 flex items-center justify-center">
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 shadow-sm">
              <svg
                className="w-6 h-6 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-green-700 font-semibold">
                Stamp fetched successfully
              </span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}...
            </div>
          ) : (
            t("addSummon")
          )}
        </button>
      </form>
    </SideModal>
  );
}
